/**
 * rc36 — Editor de plantilla del RECIBO DE PAGO para miniprinter térmica 80mm.
 *
 * Difiere de PlantillaFacturaSection en que:
 *  - El recibo es HTML directo (no PDF) porque se imprime via window.print()
 *  - El preview abre en ventana nueva con el HTML rendereado, sin auto-print
 *  - El ancho objetivo es 80mm — sugerido CSS @page { size: 80mm auto }
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Eye, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  useConfigByGrupo, useUpdateConfig, previewPlantillaRecibo,
} from "@/features/config/api";
import type { ApiError } from "@/lib/api";

const VARIABLES_DISPONIBLES: { key: string; desc: string }[] = [
  { key: "{{ isp.nombre_comercial }}", desc: "Nombre comercial del ISP" },
  { key: "{{ isp.nombre_legal }}", desc: "Razón social del ISP" },
  { key: "{{ isp.rfc }}", desc: "RFC del ISP" },
  { key: "{{ isp.domicilio_fiscal }}", desc: "Domicilio fiscal" },
  { key: "{{ isp.telefono }}", desc: "Teléfono" },
  { key: "{{ isp.logo_url }}", desc: "URL logo (si configurada)" },
  { key: "{{ cliente.razon_social }}", desc: "Razón social del cliente" },
  { key: "{{ cliente.codigo }}", desc: "Código del cliente (RES-XXX)" },
  { key: "{{ cliente.rfc }}", desc: "RFC del cliente (vacío si genérico)" },
  { key: "{% if contrato %}", desc: "Bloque si hay contrato" },
  { key: "{{ contrato.codigo }}", desc: "Código del contrato (CON-XXX)" },
  { key: "{{ contrato.pppoe_user }}", desc: "Usuario PPPoE" },
  { key: "{% endif %}", desc: "Fin bloque if" },
  { key: "{% if factura %}", desc: "Bloque si pago aplicado a factura" },
  { key: "{{ factura.folio }}", desc: "Folio factura cubierta (F-XXX)" },
  { key: "{{ factura.total }}", desc: "Total de la factura" },
  { key: "{{ factura.saldo_previo }}", desc: "Saldo antes del pago" },
  { key: "{{ factura.saldo_actual }}", desc: "Saldo después del pago" },
  { key: "{{ pago.folio }}", desc: "Folio del pago (P-XXX)" },
  { key: "{{ pago.fecha_hora }}", desc: "Fecha y hora del pago (TZ MX)" },
  { key: "{{ pago.metodo_label }}", desc: "EFECTIVO / TRANSFERENCIA / etc." },
  { key: "{{ pago.monto }}", desc: "Monto en número" },
  { key: "{{ pago.moneda }}", desc: "MXN" },
  { key: "{{ pago.monto_en_letras }}", desc: "Monto en letras" },
  { key: "{{ pago.cadena_pago }}", desc: "SPEI/Toku/folio interno" },
  { key: "{% if cajero %}", desc: "Bloque si pago registrado por usuario" },
  { key: "{{ cajero.nombre }}", desc: "Nombre del cajero" },
  { key: "{% endif %}", desc: "Fin bloque cajero" },
  { key: "{{ fecha_hoy }}", desc: "Fecha actual (DD/MM/YYYY)" },
  { key: "{{ hora_hoy }}", desc: "Hora actual (HH:MM:SS)" },
];

export function PlantillaReciboPagoSection() {
  const { data: items, isLoading } = useConfigByGrupo("recibo_pago");
  const updateMut = useUpdateConfig();
  const [plantilla, setPlantilla] = useState<string>("");
  const [dirty, setDirty] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  useEffect(() => {
    if (items) {
      const item = items.find((i) => i.key === "recibo_pago.plantilla_html");
      setPlantilla(typeof item?.value === "string" ? item.value : "");
      setDirty(false);
    }
  }, [items]);

  async function handleSave() {
    try {
      await updateMut.mutateAsync({
        key: "recibo_pago.plantilla_html",
        value: plantilla,
        value_type: "string",
        grupo: "recibo_pago",
      });
      toast.success("Plantilla de recibo guardada");
      setDirty(false);
    } catch (e) {
      toast.error("Error al guardar", { description: (e as ApiError).message });
    }
  }

  async function handlePreview() {
    setPreviewing(true);
    try {
      const html = await previewPlantillaRecibo(plantilla);
      // Abrir HTML en nueva ventana. Inyectar param ?autoprint=0 vía hash de búsqueda
      // no es viable porque el body ya viene completo; en su lugar abrimos un blob.
      // El script default ya respeta URLSearchParams del location, pero al ser blob://
      // hay que truquearle agregando ?autoprint=0 al window.open URL — no se respeta
      // en blob. Solución: hacer wrap que setea sessionStorage flag.
      const wrapped = html.replace(
        "</head>",
        `<script>window._preview=true;</script></head>`,
      );
      // Reemplazar el window.print del default si está → preview no imprime
      const noAutoPrint = wrapped.replace(
        /setTimeout\(function\(\)\{\s*window\.print\(\);\s*\},\s*\d+\);?/g,
        "/* preview-mode: no auto-print */",
      );
      // rc134 — SEGURIDAD: antes se abría como blob same-origin (los <script>
      // de la plantilla corrían con la sesión del usuario). Ahora va en un
      // <iframe sandbox> sin allow-same-origin: se ve igual, no toca nada.
      setPreviewHtml(noAutoPrint);
    } catch (e) {
      toast.error("Error al generar preview", { description: (e as Error).message });
    } finally {
      setPreviewing(false);
    }
  }

  function insertVariable(v: string) {
    setPlantilla((prev) => prev + v);
    setDirty(true);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <div>
          Plantilla del <b>recibo de pago</b> para miniprinter térmica <b>80mm</b>.
          Sintaxis <b>Jinja2</b> sobre HTML con CSS{" "}
          <code>@page {"{ size: 80mm auto; }"}</code>. Se imprime con{" "}
          <code>window.print()</code> directo desde el navegador del cajero —
          configura tu miniprinter como impresora default.
          <br />
          El <b>Preview</b> abre el ticket renderizado en ventana nueva sin
          imprimir automático (puedes usar Ctrl/Cmd+P para probar la impresión).
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_260px]">
        <Textarea
          value={plantilla}
          onChange={(e) => { setPlantilla(e.target.value); setDirty(true); }}
          rows={28}
          className="font-mono text-[11px]"
          spellCheck={false}
        />

        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold">Variables disponibles</h3>
          <p className="text-[10px] text-muted-foreground">
            Clic para insertar al final.
          </p>
          <div className="flex max-h-[620px] flex-col gap-0.5 overflow-y-auto rounded border p-1">
            {VARIABLES_DISPONIBLES.map((v) => (
              <button
                key={v.key}
                type="button"
                onClick={() => insertVariable(v.key)}
                className="flex flex-col items-start gap-0 rounded px-1.5 py-1 text-left text-[10px] hover:bg-accent"
              >
                <code className="font-mono">{v.key}</code>
                <span className="text-muted-foreground">{v.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {dirty && (
          <span className="text-xs text-muted-foreground">Cambios sin guardar</span>
        )}
        <Button size="sm" variant="outline" onClick={handlePreview} disabled={previewing}>
          <Eye size={12} />
          {previewing ? "Generando…" : "Ver preview"}
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!dirty || updateMut.isPending}>
          <Save size={12} />
          {updateMut.isPending ? "Guardando…" : "Guardar plantilla"}
        </Button>
      </div>

      {/* rc134 — preview aislado: iframe sandbox (sin allow-same-origin) */}
      <Dialog open={previewHtml !== null} onOpenChange={(o) => { if (!o) setPreviewHtml(null); }}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Vista previa del recibo</DialogTitle>
            <DialogDescription>Así se ve en la miniprinter (80 mm). No imprime desde aquí.</DialogDescription>
          </DialogHeader>
          {previewHtml !== null && (
            <iframe
              title="Vista previa del recibo"
              sandbox="allow-scripts"
              srcDoc={previewHtml}
              className="h-[70vh] w-full rounded border bg-white"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
